/**
 * The dashboard (start page): a greeting and blocks in a three-column grid.
 * "Aanpassen" adds, moves (arrows or dragging the grip), resizes, configures and removes blocks; the layout
 * is a synced workspace setting.
 */
import { MemosBlock } from '../../memos/MemosBlock';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DropdownMenu, Popover } from 'radix-ui';
import {
  ArrowLeft,
  ArrowRight,
  CalendarDays,
  Check,
  Clock,
  NotebookPen,
  Table2,
  GripVertical,
  Pencil,
  Plus,
  Settings2,
  Trash2,
} from 'lucide-react';
import {
  addBlock,
  BLOCK_TYPES,
  DASHBOARD_SETTING,
  defaultDashboard,
  moveBlock,
  moveBlockTo,
  addPluginBlock,
  readDashboard,
  removeBlock,
  updateBlock,
  upgradeDashboard,
  DASHBOARD_V2,
  type BlockType,
  type BlockWidth,
  type DashboardBlock,
  type DashboardLayout,
  type TasksScope,
} from '@nexus/core';
import { useStore, useStoreQuery } from '../../db/context';
import { useDatabaseList } from '../../db/database';
import { cn } from '../../lib/cn';
import { SummaryBlock } from '../../ai/SummaryBlock';
import { usePlugins } from '../../plugins/runtime';
import { PluginBlock } from '../../plugins/PluginBlock';
import {
  CalendarBlock,
  DatabaseBlock,
  NoteBlock,
  ProjectsBlock,
  RecentBlock,
  TasksBlock,
} from './blocks';
import { DashboardCapture, DayBlock, ProgressBlock } from './today';
import { ModuleIcon } from '../ModuleIcon';
import type { Module } from '../../lib/modules';
import type { LucideIcon } from 'lucide-react';
import { CloseButton } from '../CloseButton';

/** Icon and colour in a block's header. */
const BLOCK_ICON: Partial<
  Record<BlockType, { kind?: string; module?: Module; icon?: LucideIcon }>
> = {
  day: { kind: 'tasks' },
  progress: { kind: 'dashboard' },
  tasks: { kind: 'tasks' },
  calendar: { module: 'tasks', icon: CalendarDays },
  projects: { kind: 'projects' },
  memos: { kind: 'memos' },
  summary: { kind: 'ai' },
  plugin: { kind: 'plugins' },
  database: { module: 'templates', icon: Table2 },
  recent: { module: 'graph', icon: Clock },
  note: { module: 'dashboard', icon: NotebookPen },
};

const SPAN: Record<BlockWidth, string> = {
  1: 'md:col-span-1',
  2: 'md:col-span-2',
  3: 'md:col-span-3',
};
const SCOPES: TasksScope[] = ['today', 'week', 'overdue', 'inbox'];
const field = 'w-full rounded-md border border-border bg-bg px-2 py-1 text-sm';
const menu =
  'z-[70] flex min-w-44 flex-col rounded-xl border border-border bg-bg p-1 text-sm shadow-pop';
const menuItem =
  'flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-left outline-none hover:bg-bg-hover data-[highlighted]:bg-bg-hover';
const iconButton =
  'rounded p-1 text-fg-muted hover:bg-bg-hover hover:text-fg disabled:opacity-30 disabled:hover:bg-transparent';

function greeting(hour: number) {
  return hour < 6 ? 'night' : hour < 12 ? 'morning' : hour < 18 ? 'afternoon' : 'evening';
}

function useBlockTitle() {
  const { t } = useTranslation();
  const databases = useDatabaseList();
  return (block: DashboardBlock) => {
    if (block.title) return block.title;
    if (block.type === 'tasks') return t(`dashboard.scopes.${block.scope ?? 'today'}`);
    if (block.type === 'database')
      return (
        databases?.find((d) => d.id === block.databaseId)?.title || t('dashboard.types.database')
      );
    return t(`dashboard.types.${block.type}`);
  };
}

function BlockSettings({
  block,
  title,
  onChange,
}: {
  block: DashboardBlock;
  title: string;
  onChange: (b: DashboardBlock) => void;
}) {
  const { t } = useTranslation();
  const databases = useDatabaseList() ?? [];
  const db = databases.find((d) => d.id === block.databaseId);
  return (
    <Popover.Root>
      <Popover.Trigger asChild>
        <button
          type="button"
          aria-label={t('dashboard.blockSettings', { title })}
          className={iconButton}
        >
          <Settings2 className="size-3.5" />
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content align="end" sideOffset={4} className={cn(menu, 'w-64 gap-2 p-3')}>
          <div className="-mt-1 -mr-1 flex justify-end">
            <Popover.Close asChild>
              <CloseButton className="size-6" />
            </Popover.Close>
          </div>
          <label className="flex flex-col gap-1 text-xs">
            {t('dashboard.title')}
            <input
              defaultValue={block.title ?? ''}
              placeholder={title}
              onBlur={(e) => onChange({ ...block, title: e.target.value.trim() || undefined })}
              onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
              className={field}
            />
          </label>
          {block.type === 'tasks' && (
            <label className="flex flex-col gap-1 text-xs">
              {t('dashboard.scope')}
              <select
                value={block.scope ?? 'today'}
                onChange={(e) => onChange({ ...block, scope: e.target.value as TasksScope })}
                className={field}
              >
                {SCOPES.map((s) => (
                  <option key={s} value={s}>
                    {t(`dashboard.scopes.${s}`)}
                  </option>
                ))}
              </select>
            </label>
          )}
          {block.type === 'database' && (
            <>
              <label className="flex flex-col gap-1 text-xs">
                {t('dashboard.database')}
                <select
                  value={block.databaseId ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...block,
                      databaseId: e.target.value || undefined,
                      viewId: undefined,
                    })
                  }
                  className={field}
                >
                  <option value="" />
                  {databases.map((d) => (
                    <option key={d.id} value={d.id}>
                      {d.title || t('page.untitled')}
                    </option>
                  ))}
                </select>
              </label>
              {db && (
                <label className="flex flex-col gap-1 text-xs">
                  {t('dashboard.view')}
                  <select
                    value={block.viewId ?? db.schema.views[0]?.id ?? ''}
                    onChange={(e) => onChange({ ...block, viewId: e.target.value })}
                    className={field}
                  >
                    {db.schema.views.map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </>
          )}
          {block.type === 'recent' && (
            <label className="flex flex-col gap-1 text-xs">
              {t('dashboard.limit')}
              <input
                type="number"
                min={1}
                max={30}
                defaultValue={block.limit ?? 8}
                onBlur={(e) =>
                  onChange({
                    ...block,
                    limit: Math.min(30, Math.max(1, Number(e.target.value) || 8)),
                  })
                }
                className={field}
              />
            </label>
          )}
          <div className="flex flex-col gap-1 text-xs">
            {t('dashboard.width')}
            <div role="radiogroup" aria-label={t('dashboard.width')} className="flex gap-1">
              {([1, 2, 3] as BlockWidth[]).map((w) => (
                <button
                  key={w}
                  type="button"
                  role="radio"
                  aria-checked={block.width === w}
                  onClick={() => onChange({ ...block, width: w })}
                  className={cn(
                    'flex-1 rounded-md border border-border px-2 py-1',
                    block.width === w && 'border-accent bg-accent/10',
                  )}
                >
                  {t(`dashboard.widths.${w}`)}
                </button>
              ))}
            </div>
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}

function BlockBody({ block }: { block: DashboardBlock }) {
  switch (block.type) {
    case 'day':
      return <DayBlock />;
    case 'progress':
      return <ProgressBlock />;
    case 'tasks':
      return <TasksBlock block={block} />;
    case 'database':
      return <DatabaseBlock block={block} />;
    case 'recent':
      return <RecentBlock block={block} />;
    case 'calendar':
      return <CalendarBlock />;
    case 'summary':
      return <SummaryBlock />;
    case 'projects':
      return <ProjectsBlock />;
    case 'memos':
      return <MemosBlock />;
    case 'plugin':
      return <PluginBlock block={block} />;
    default:
      return <NoteBlock />;
  }
}

export function DashboardView() {
  const { t, i18n } = useTranslation();
  const store = useStore();
  const titleOf = useBlockTitle();
  const plugins = usePlugins();
  const pluginBlocks = (plugins ?? [])
    .filter((p) => p.enabled)
    .flatMap((p) => p.manifest.blocks.map((b) => ({ ...b, pluginId: p.pageId })));
  const [editing, setEditing] = useState(false);
  const [dragId, setDragId] = useState<string | null>(null);
  const [over, setOver] = useState<{ id: string; side: 'before' | 'after' } | null>(null);
  const layout = useStoreQuery(
    async (s) =>
      readDashboard(await s.getSetting(DASHBOARD_SETTING)) ??
      defaultDashboard({ meetingsDbId: await s.getSetting('meetings_db_id') }),
    [],
    ['settings'],
  );
  // Changes are applied one after another to the latest stored layout.
  const queue = useRef<Promise<void>>(Promise.resolve());
  // Once per workspace: a stored layout gets "Mijn dag", progress and Onthouden.
  useEffect(() => {
    void (async () => {
      if (await store.getSetting(DASHBOARD_V2)) return;
      const stored = readDashboard(await store.getSetting(DASHBOARD_SETTING));
      if (stored)
        await store.setSetting(DASHBOARD_SETTING, JSON.stringify(upgradeDashboard(stored)));
      await store.setSetting(DASHBOARD_V2, String(Date.now()));
    })();
  }, [store]);
  if (!layout) return null;
  const save = (change: (l: DashboardLayout) => DashboardLayout) => {
    queue.current = queue.current
      .then(async () => {
        const latest =
          readDashboard(await store.getSetting(DASHBOARD_SETTING)) ??
          defaultDashboard({ meetingsDbId: await store.getSetting('meetings_db_id') });
        await store.setSetting(DASHBOARD_SETTING, JSON.stringify(change(latest)));
      })
      .catch((e: unknown) => console.error('dashboard', e));
  };

  const now = new Date();
  const date = new Intl.DateTimeFormat(i18n.language, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).format(now);

  return (
    <div className="mx-auto w-full max-w-6xl px-6 py-6 sm:px-10" data-testid="dashboard">
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {t(`dashboard.greeting.${greeting(now.getHours())}`)}
          </h1>
          <p className="mt-1 text-sm text-fg-muted first-letter:uppercase">{date}</p>
        </div>
        <div className="flex items-center gap-2">
          {editing && (
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <button
                  type="button"
                  className="flex items-center gap-1 rounded-full border border-border px-3 py-1 text-sm hover:bg-bg-hover"
                >
                  <Plus className="size-4" />
                  {t('dashboard.addBlock')}
                </button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Portal>
                <DropdownMenu.Content align="end" sideOffset={4} className={menu}>
                  {BLOCK_TYPES.map((type: BlockType) => (
                    <DropdownMenu.Item
                      key={type}
                      className={menuItem}
                      onSelect={() => save((l) => addBlock(l, type).layout)}
                    >
                      {t(`dashboard.types.${type}`)}
                    </DropdownMenu.Item>
                  ))}
                  {pluginBlocks.length > 0 && (
                    <DropdownMenu.Separator className="my-1 h-px bg-border" />
                  )}
                  {pluginBlocks.map((b) => (
                    <DropdownMenu.Item
                      key={`${b.pluginId}:${b.id}`}
                      className={menuItem}
                      onSelect={() => save((l) => addPluginBlock(l, b.pluginId, b.id, b.title))}
                    >
                      🧩 {b.title}
                    </DropdownMenu.Item>
                  ))}
                </DropdownMenu.Content>
              </DropdownMenu.Portal>
            </DropdownMenu.Root>
          )}
          <button
            type="button"
            aria-pressed={editing}
            onClick={() => setEditing((v) => !v)}
            className={cn(
              'flex items-center gap-1 rounded-full px-3 py-1 text-sm',
              editing ? 'bg-accent font-medium text-accent-fg' : 'text-fg-muted hover:bg-bg-hover',
            )}
          >
            {editing ? <Check className="size-4" /> : <Pencil className="size-4" />}
            {editing ? t('dashboard.done') : t('dashboard.customize')}
          </button>
        </div>
      </header>

      <DashboardCapture />

      {layout.blocks.length === 0 && (
        <p className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-fg-muted">
          {t('dashboard.empty')}
        </p>
      )}

      {/* Bento: tiles of different sizes; "Mijn dag" is two rows high. */}
      <div className="grid grid-cols-1 gap-4 md:grid-flow-row-dense md:grid-cols-3">
        {layout.blocks.map((block, i) => {
          const title = titleOf(block);
          return (
            <section
              key={block.id}
              aria-label={title}
              data-testid="dashboard-block"
              data-block-id={block.id}
              onDragOver={(e) => {
                if (!dragId || dragId === block.id) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                const r = e.currentTarget.getBoundingClientRect();
                const side = e.clientX < r.left + r.width / 2 ? 'before' : 'after';
                if (over?.id !== block.id || over.side !== side) setOver({ id: block.id, side });
              }}
              onDragLeave={(e) => {
                if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOver(null);
              }}
              onDrop={(e) => {
                e.preventDefault();
                if (dragId && over) save((l) => moveBlockTo(l, dragId, over.id, over.side));
                setDragId(null);
                setOver(null);
              }}
              className={cn(
                'flex min-w-0 flex-col rounded-2xl border border-border bg-bg p-4 transition-shadow hover:shadow-sheet',
                SPAN[block.width],
                block.type === 'day' && 'md:row-span-2',
                // Onthouden as a note on the board.
                block.type === 'memos' && 'mod-projects mod-soft border-transparent',
                editing && 'ring-1 ring-accent/40',
                dragId === block.id && 'opacity-40',
                over?.id === block.id &&
                  (over.side === 'before'
                    ? 'shadow-[-4px_0_0_0_var(--color-accent)]'
                    : 'shadow-[4px_0_0_0_var(--color-accent)]'),
              )}
            >
              {(block.type !== 'database' || editing) && (
                <div className="mb-3 flex items-center gap-2">
                  {editing && (
                    <span
                      draggable
                      role="button"
                      tabIndex={-1}
                      aria-label={t('dashboard.drag', { title })}
                      title={t('dashboard.drag', { title })}
                      onDragStart={(e) => {
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', block.id);
                        const section = e.currentTarget.closest('section');
                        if (section) e.dataTransfer.setDragImage(section, 16, 16);
                        setDragId(block.id);
                      }}
                      onDragEnd={() => {
                        setDragId(null);
                        setOver(null);
                      }}
                      className="-ml-1 cursor-grab rounded p-0.5 text-fg-muted hover:bg-bg-hover hover:text-fg active:cursor-grabbing"
                    >
                      <GripVertical className="size-4" />
                    </span>
                  )}
                  <ModuleIcon {...(BLOCK_ICON[block.type] ?? {})} size="xs" />
                  <h2 className="min-w-0 flex-1 truncate text-sm font-semibold">{title}</h2>
                  {editing && (
                    <>
                      <button
                        type="button"
                        aria-label={t('dashboard.moveEarlier', { title })}
                        disabled={i === 0}
                        onClick={() => save((l) => moveBlock(l, block.id, -1))}
                        className={iconButton}
                      >
                        <ArrowLeft className="size-3.5" />
                      </button>
                      <button
                        type="button"
                        aria-label={t('dashboard.moveLater', { title })}
                        disabled={i === layout.blocks.length - 1}
                        onClick={() => save((l) => moveBlock(l, block.id, 1))}
                        className={iconButton}
                      >
                        <ArrowRight className="size-3.5" />
                      </button>
                      <BlockSettings
                        block={block}
                        title={title}
                        onChange={(next) => save((l) => updateBlock(l, next))}
                      />
                      <button
                        type="button"
                        aria-label={t('dashboard.removeBlock', { title })}
                        onClick={() => save((l) => removeBlock(l, block.id))}
                        className={cn(iconButton, 'hover:text-danger')}
                      >
                        <Trash2 className="size-3.5" />
                      </button>
                    </>
                  )}
                </div>
              )}
              <BlockBody block={block} />
            </section>
          );
        })}
      </div>
    </div>
  );
}
